/**
 * Aggregated revenues memoized selectors
 */

import { createSelector } from '@reduxjs/toolkit';

import { getSelectedStoresIds, selectIsTaxIncluded } from '../../Preferences';
import { getEvolution } from '../helpers';

/**
 * Get data about aggregated revenues retrieved from api
 *
 * @return {Object}
 */
export const getRevenuesAggregatedEntities = ({ entities }) => entities.revenuesAggregated.entities;

/**
 * Get status about aggregated revenues request
 * `none`, `loading`, `loaded` or `failed`
 *
 * @return {string}
 */
export const getRevenuesAggregatedStatus = ({ entities }) => entities.revenuesAggregated.status;

/**
 * If last attempt at fetching data resulted in an error, returns the error value
 *
 * @return {string}
 */
export const getRevenuesAggregatedError = ({ entities }) => entities.revenuesAggregated.error;

/**
 * If data is being fetched
 *
 * @return {boolean}
 */
export const areRevenuesAggregatedLoading = createSelector(
  [getRevenuesAggregatedStatus],
  status => status === 'loading',
);

/**
 * Get revenues of selected stores
 *
 * @return {Array}
 */
export const getDataOfSelectedStores = createSelector(
  [getRevenuesAggregatedEntities, getSelectedStoresIds],
  (entities, storeIds) =>
    entities && storeIds
      ? Object.keys(entities)
          .filter(id => storeIds.includes(parseInt(id, 10)))
          .map(id => ({ ...entities[id], id }))
      : [],
);

/**
 * Get revenues data of selected stores and selected period
 * If there is no data, the api does not return the `selected` key, so we check the existence of the key
 *
 * @return {Array}
 */
export const getSelectedDataKpiOfSelectedStores = createSelector([getDataOfSelectedStores], data =>
  data.map(({ id, selected }) => ({
    ...{
      value: {
        tax_excluded: selected && selected.value.tax_excluded ? selected.value.tax_excluded : 0,
        tax_included: selected && selected.value.tax_included ? selected.value.tax_included : 0,
      },
    },
    id,
  })),
);

/**
 * Calculates selected revenues without tax on selected stores
 *
 * @return {number}
 */
export const getSelectedDataTaxExcluded = createSelector(
  [getSelectedDataKpiOfSelectedStores],
  data => data.reduce((acc, { value }) => acc + value.tax_excluded, 0),
);

/**
 * Calculates selected revenues with tax on selected stores
 *
 * @return {number}
 */
export const getSelectedDataTaxIncluded = createSelector(
  [getSelectedDataKpiOfSelectedStores],
  data => data.reduce((acc, { value }) => acc + value.tax_included, 0),
);

/**
 * Provides revenues on selected stores and selected period
 *
 * @return {number}
 */
export const getSelectedRevenuesAggregated = createSelector(
  [selectIsTaxIncluded, getSelectedDataTaxIncluded, getSelectedDataTaxExcluded],
  (isTaxIncl, dataTaxIncluded, dataTaxExcluded) => (isTaxIncl ? dataTaxIncluded : dataTaxExcluded),
);

/**
 * Get revenues data of selected stores and reference period
 * If there is no data, the api does not return the `reference` key, so we check the existence of the key
 *
 * @return {Array}
 */
export const getReferenceDataKpiOfSelectedStores = createSelector([getDataOfSelectedStores], data =>
  data.map(({ id, reference }) => ({
    ...{
      value: {
        tax_excluded: reference && reference.value.tax_excluded ? reference.value.tax_excluded : 0,
        tax_included: reference && reference.value.tax_included ? reference.value.tax_included : 0,
      },
    },
    id,
  })),
);

/**
 * Calculates reference revenues without tax on selected stores
 *
 * @return {number}
 */
export const getReferenceDataTaxExcluded = createSelector(
  [getReferenceDataKpiOfSelectedStores],
  data => data.reduce((acc, { value }) => acc + value.tax_excluded, 0),
);

/**
 * Calculates reference revenues with tax on selected stores
 *
 * @return {number}
 */
export const getReferenceDataTaxIncluded = createSelector(
  [getReferenceDataKpiOfSelectedStores],
  data => data.reduce((acc, { value }) => acc + value.tax_included, 0),
);

/**
 * Provides revenues on selected stores and reference period
 *
 * @return {number}
 */
export const getReferenceRevenuesAggregated = createSelector(
  [selectIsTaxIncluded, getReferenceDataTaxExcluded, getReferenceDataTaxIncluded],
  (tax, dataTaxExcluded, dataTaxIncluded) => (!tax ? dataTaxExcluded : dataTaxIncluded),
);

/**
 * Get evolution of revenues from datas of reference period and selected period
 * Ratio between reference and selected datas
 *
 * @return {number}
 */
export const getEvolutionRevenuesAggregated = createSelector(
  // pick of a piece of state
  [getReferenceRevenuesAggregated, getSelectedRevenuesAggregated],
  // function that has our select logic
  (reference, selected) => getEvolution(selected, reference),
);
